/**
 * Shifts Drawer Component
 * Manages work shifts configuration for facilities
 * Includes smart defaults, validation, and inline editing
 */

import React, { useState, useEffect } from 'react';
import {
  X,
  ChevronDown,
  ChevronUp,
  Plus,
  Trash2,
  Clock,
  Check,
  AlertTriangle,
  AlertCircle,
  CheckCircle2,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useFeedbackContext } from '../../context';
import { serviceLogger } from '../../utils/serviceLogger';

// Default 3 shifts
export const DEFAULT_SHIFTS = [
  {
    id: '1',
    name: 'Morning Shift',
    startTime: '06:00',
    endTime: '15:00',
    duration: 9,
    color: '#FFD700',
  },
  {
    id: '2',
    name: 'Evening Shift',
    startTime: '15:00',
    endTime: '00:00',
    duration: 9,
    color: '#FF8C00',
  },
  {
    id: '3',
    name: 'Night Shift',
    startTime: '00:00',
    endTime: '06:00',
    duration: 6,
    color: '#4B0082',
  },
];

export const ShiftsDrawer = ({ isOpen, onClose, onSave, initialShifts }) => {
  const { showToast } = useFeedbackContext();

  const [shifts, setShifts] = useState(initialShifts || DEFAULT_SHIFTS);
  const [expandedShiftId, setExpandedShiftId] = useState(null);
  const [editingShiftId, setEditingShiftId] = useState(null);

  // Form state for editing
  const [editName, setEditName] = useState('');
  const [editStartTime, setEditStartTime] = useState('');
  const [editEndTime, setEditEndTime] = useState('');

  // CRITICAL FIX: Reset shifts when drawer opens or initialShifts changes
  useEffect(() => {
    if (isOpen) {
      serviceLogger.log('[ShiftsDrawer] RESETTING SHIFTS for initial:', initialShifts);
      setShifts(initialShifts || DEFAULT_SHIFTS);
      setExpandedShiftId(null);
      setEditingShiftId(null);
      serviceLogger.log('[ShiftsDrawer] Shifts reset complete');
    }
  }, [isOpen, initialShifts]);

  // Body scroll lock
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Calculate duration in hours
  const calculateDuration = (start, end) => {
    const [startHour, startMin] = start.split(':').map(Number);
    const [endHour, endMin] = end.split(':').map(Number);

    let startMinutes = startHour * 60 + startMin;
    let endMinutes = endHour * 60 + endMin;

    // Handle overnight shifts (end time is next day)
    if (endMinutes <= startMinutes) {
      endMinutes += 24 * 60; // Add 24 hours
    }

    const durationMinutes = endMinutes - startMinutes;
    return durationMinutes / 60;
  };

  // Validate time (end must be after start, allowing overnight)
  const isTimeValid = (start, end) => {
    const duration = calculateDuration(start, end);
    return duration > 0 && duration <= 24;
  };

  // Check for overlaps
  const hasOverlap = (shiftId, start, end) => {
    const otherShifts = shifts.filter((s) => s.id !== shiftId);

    for (const shift of otherShifts) {
      // Convert times to minutes for easier comparison
      const [startHour, startMin] = start.split(':').map(Number);
      const [endHour, endMin] = end.split(':').map(Number);
      const [shiftStartHour, shiftStartMin] = shift.startTime.split(':').map(Number);
      const [shiftEndHour, shiftEndMin] = shift.endTime.split(':').map(Number);

      let startMinutes = startHour * 60 + startMin;
      let endMinutes = endHour * 60 + endMin;
      let shiftStartMinutes = shiftStartHour * 60 + shiftStartMin;
      let shiftEndMinutes = shiftEndHour * 60 + shiftEndMin;

      // Normalize overnight shifts
      if (endMinutes <= startMinutes) endMinutes += 24 * 60;
      if (shiftEndMinutes <= shiftStartMinutes) shiftEndMinutes += 24 * 60;

      // Check overlap
      if (
        (startMinutes < shiftEndMinutes && endMinutes > shiftStartMinutes) ||
        (shiftStartMinutes < endMinutes && shiftEndMinutes > startMinutes)
      ) {
        return true;
      }
    }

    return false;
  };

  // Calculate total coverage
  const calculateCoverage = () => {
    const totalMinutes = shifts.reduce((sum, shift) => sum + shift.duration * 60, 0);
    return totalMinutes / 60;
  };

  const coverage = calculateCoverage();
  const fullCoverage = coverage === 24;

  // Handle edit shift
  const handleEditShift = (shift) => {
    setEditingShiftId(shift.id);
    setExpandedShiftId(shift.id);
    setEditName(shift.name);
    setEditStartTime(shift.startTime);
    setEditEndTime(shift.endTime);
  };

  // Handle save edited shift
  const handleSaveEdit = () => {
    if (!editingShiftId) return;

    if (!editName.trim()) {
      showToast({ variant: 'error', title: 'Error', copy: 'Shift name is required' });
      return;
    }

    if (!isTimeValid(editStartTime, editEndTime)) {
      showToast({ variant: 'error', title: 'Error', copy: 'Invalid time range' });
      return;
    }

    if (hasOverlap(editingShiftId, editStartTime, editEndTime)) {
      showToast({
        variant: 'error',
        title: 'Error',
        copy: 'This shift overlaps with another shift',
      });
      return;
    }

    const duration = calculateDuration(editStartTime, editEndTime);

    setShifts((prev) =>
      prev.map((shift) =>
        shift.id === editingShiftId
          ? {
              ...shift,
              name: editName,
              startTime: editStartTime,
              endTime: editEndTime,
              duration,
            }
          : shift,
      ),
    );

    setEditingShiftId(null);
    setExpandedShiftId(null);
    showToast({ variant: 'success', title: 'Success', copy: 'Shift updated' });
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditingShiftId(null);
    setExpandedShiftId(null);
  };

  // Handle delete shift
  const handleDeleteShift = (shiftId) => {
    if (shifts.length <= 1) {
      showToast({ variant: 'error', title: 'Error', copy: 'You must have at least one shift' });
      return;
    }

    setShifts((prev) => prev.filter((s) => s.id !== shiftId));
    setEditingShiftId(null);
    setExpandedShiftId(null);
    showToast({ variant: 'success', title: 'Success', copy: 'Shift deleted' });
  };

  // Handle add custom shift
  const handleAddShift = () => {
    const newShift = {
      id: `shift-${Date.now()}`,
      name: 'Custom Shift',
      startTime: '08:00',
      endTime: '17:00',
      duration: 9,
      color: '#61b8e7',
    };

    setShifts((prev) => [...prev, newShift]);
    handleEditShift(newShift);
    showToast({ variant: 'success', title: 'Success', copy: 'New shift added' });
  };

  // Handle save all shifts
  const handleSaveShifts = () => {
    onSave(shifts);
    showToast({ variant: 'success', title: 'Success', copy: 'Work shifts saved successfully' });
    onClose();
  };

  // Format time for display (12h format with AM/PM)
  const formatTime = (time24) => {
    const [hour, min] = time24.split(':').map(Number);
    const period = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${hour12.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')} ${period}`;
  };

  // Toggle expand/collapse
  const toggleExpand = (shiftId) => {
    if (editingShiftId === shiftId) return; // Don't collapse if editing
    setExpandedShiftId((prev) => (prev === shiftId ? null : shiftId));
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/50 z-[100]"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          />

          {/* Drawer */}
          <motion.div
            className="fixed top-0 right-0 h-full w-full max-w-[620px] bg-white z-[110] shadow-2xl"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{
              type: 'spring',
              damping: 25,
              stiffness: 200,
              mass: 0.8,
            }}
          >
            <div className="h-full flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between px-8 py-6 border-b border-gray-200">
                <div>
                  <h2 className="font-['Roboto:Bold',sans-serif] font-bold text-[24px] text-black">
                    Set Work Shifts
                  </h2>
                  <p className="font-['Roboto:Regular',sans-serif] text-[13px] text-gray-500 mt-1">
                    Configure cleaning shift schedules for this facility
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                  type="button"
                >
                  <X className="size-5 text-gray-600" />
                </button>
              </div>

              {/* Coverage Indicator */}
              <div
                className={`px-8 py-4 border-b flex items-center justify-between ${
                  fullCoverage ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'
                }`}
              >
                <div className="flex items-center gap-2">
                  {fullCoverage ? (
                    <CheckCircle2 className="w-5 h-5 text-green-600" strokeWidth={2.5} />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-amber-600" strokeWidth={2.5} />
                  )}
                  <span className="font-['Roboto:Medium',sans-serif] text-[14px] text-gray-800">
                    24-Hour Coverage:
                  </span>
                </div>
                <span
                  className={`font-['Roboto:Bold',sans-serif] text-[16px] ${
                    fullCoverage ? 'text-green-700' : 'text-amber-700'
                  }`}
                >
                  {coverage.toFixed(1)}/24 hours{' '}
                  {fullCoverage ? (
                    <Check className="w-5 h-5 inline shrink-0 align-middle" aria-hidden />
                  ) : (
                    <AlertTriangle className="w-5 h-5 inline shrink-0 align-middle" aria-hidden />
                  )}
                </span>
              </div>

              {/* Content - Scrollable */}
              <div className="flex-1 overflow-y-auto px-8 py-6">
                <div className="space-y-3">
                  {shifts.map((shift) => {
                    const isEditing = editingShiftId === shift.id;
                    const isExpanded = expandedShiftId === shift.id;

                    const currentDuration = isEditing
                      ? calculateDuration(editStartTime, editEndTime)
                      : shift.duration;

                    const isValid = isEditing
                      ? isTimeValid(editStartTime, editEndTime) &&
                        !hasOverlap(shift.id, editStartTime, editEndTime)
                      : true;

                    return (
                      <div
                        key={shift.id}
                        className="border border-gray-200 rounded-lg overflow-hidden bg-white"
                      >
                        {/* Shift Header */}
                        <button
                          type="button"
                          onClick={() => toggleExpand(shift.id)}
                          disabled={isEditing}
                          className={`w-full px-5 py-4 flex items-center justify-between transition-colors ${
                            isEditing ? 'cursor-default' : 'hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className="w-4 h-4 rounded-full"
                              style={{ backgroundColor: shift.color }}
                            />
                            <div className="text-left">
                              <h3 className="font-['Roboto:SemiBold',sans-serif] font-semibold text-[16px] text-black">
                                {isEditing ? editName : shift.name}
                              </h3>
                              <p className="font-['Roboto:Regular',sans-serif] text-[13px] text-gray-500">
                                {isEditing
                                  ? formatTime(editStartTime)
                                  : formatTime(shift.startTime)}{' '}
                                → {isEditing ? formatTime(editEndTime) : formatTime(shift.endTime)}{' '}
                                • {currentDuration.toFixed(1)}h
                              </p>
                            </div>
                          </div>

                          {!isEditing &&
                            (isExpanded ? (
                              <ChevronUp className="w-5 h-5 text-gray-500" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-gray-500" />
                            ))}
                        </button>

                        {/* Expanded Content */}
                        <AnimatePresence>
                          {isExpanded && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden"
                            >
                              <div className="bg-gray-50 px-5 py-5 space-y-4 border-t border-gray-200">
                                {isEditing ? (
                                  <>
                                    {/* Edit Mode */}
                                    <div>
                                      <label className="block font-['Roboto:Medium',sans-serif] text-[14px] text-gray-700 mb-2">
                                        Shift Name
                                      </label>
                                      <input
                                        type="text"
                                        value={editName}
                                        onChange={(e) => setEditName(e.target.value)}
                                        className="w-full border-2 border-gray-300 rounded-lg px-4 py-2.5 font-['Roboto:Regular',sans-serif] text-[14px] focus:border-[#61b8e7] focus:outline-none"
                                        placeholder="Enter shift name"
                                      />
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                      <div>
                                        <label className="block font-['Roboto:Medium',sans-serif] text-[14px] text-gray-700 mb-2">
                                          Start Time
                                        </label>
                                        <input
                                          type="time"
                                          value={editStartTime}
                                          onChange={(e) => setEditStartTime(e.target.value)}
                                          step="60"
                                          inputMode="numeric"
                                          className="w-full border-2 border-gray-300 rounded-lg px-4 py-2.5 font-['Roboto:Regular',sans-serif] text-[14px] focus:border-[#61b8e7] focus:outline-none cursor-text"
                                          title="Enter time in HH:MM format (e.g., 09:00)"
                                        />
                                      </div>
                                      <div>
                                        <label className="block font-['Roboto:Medium',sans-serif] text-[14px] text-gray-700 mb-2">
                                          End Time
                                        </label>
                                        <input
                                          type="time"
                                          value={editEndTime}
                                          onChange={(e) => setEditEndTime(e.target.value)}
                                          step="60"
                                          inputMode="numeric"
                                          className="w-full border-2 border-gray-300 rounded-lg px-4 py-2.5 font-['Roboto:Regular',sans-serif] text-[14px] focus:border-[#61b8e7] focus:outline-none cursor-text"
                                          title="Enter time in HH:MM format (e.g., 17:00)"
                                        />
                                      </div>
                                    </div>

                                    {/* Duration Display */}
                                    <div className="bg-white border border-gray-200 rounded-lg p-3 flex items-center gap-2">
                                      <Clock className="w-4 h-4 text-gray-500" />
                                      <span className="font-['Roboto:Regular',sans-serif] text-[14px] text-gray-700">
                                        Duration:{' '}
                                        <strong>{currentDuration.toFixed(1)} hours</strong>
                                        {currentDuration > 12 && ' (overnight shift)'}
                                      </span>
                                    </div>

                                    {/* Validation Messages */}
                                    {!isValid && (
                                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
                                        <AlertCircle
                                          className="w-4 h-4 text-red-600"
                                          strokeWidth={2.5}
                                        />
                                        <span className="font-['Roboto:Medium',sans-serif] text-[13px] text-red-700">
                                          {hasOverlap(shift.id, editStartTime, editEndTime)
                                            ? 'This shift overlaps with another shift'
                                            : 'Invalid time range'}
                                        </span>
                                      </div>
                                    )}

                                    {/* Action Buttons */}
                                    <div className="flex items-center justify-between pt-2">
                                      <button
                                        type="button"
                                        onClick={() => handleDeleteShift(shift.id)}
                                        className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors font-['Roboto:Medium',sans-serif] text-[14px]"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                        Delete Shift
                                      </button>

                                      <div className="flex items-center gap-2">
                                        <button
                                          type="button"
                                          onClick={handleCancelEdit}
                                          className="px-4 py-2 border-2 border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-['Roboto:Medium',sans-serif] text-[14px]"
                                        >
                                          Cancel
                                        </button>
                                        <button
                                          type="button"
                                          onClick={handleSaveEdit}
                                          disabled={!isValid}
                                          className={`px-4 py-2 rounded-lg font-['Roboto:SemiBold',sans-serif] text-[14px] transition-colors ${
                                            isValid
                                              ? 'bg-[#61b8e7] hover:bg-[#4da3d1] text-white'
                                              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                          }`}
                                        >
                                          Save Shift
                                        </button>
                                      </div>
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    {/* View Mode */}
                                    <div className="space-y-3">
                                      <div className="flex items-center justify-between">
                                        <span className="font-['Roboto:Medium',sans-serif] text-[13px] text-gray-600">
                                          Duration:
                                        </span>
                                        <span className="font-['Roboto:SemiBold',sans-serif] text-[14px] text-gray-900">
                                          {shift.duration.toFixed(1)} hours
                                        </span>
                                      </div>
                                      <div className="flex items-center justify-between">
                                        <span className="font-['Roboto:Medium',sans-serif] text-[13px] text-gray-600">
                                          Time Range:
                                        </span>
                                        <span className="font-['Roboto:Regular',sans-serif] text-[14px] text-gray-900">
                                          {formatTime(shift.startTime)} -{' '}
                                          {formatTime(shift.endTime)}
                                        </span>
                                      </div>
                                    </div>

                                    <button
                                      type="button"
                                      onClick={() => handleEditShift(shift)}
                                      className="w-full py-2 bg-white border-2 border-[#61b8e7] text-[#61b8e7] rounded-lg hover:bg-[#f0f9ff] transition-colors font-['Roboto:SemiBold',sans-serif] text-[14px]"
                                    >
                                      Edit Shift
                                    </button>
                                  </>
                                )}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
                </div>

                {/* Add Custom Shift Button */}
                <button
                  type="button"
                  onClick={handleAddShift}
                  className="w-full mt-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-[#61b8e7] hover:text-[#61b8e7] hover:bg-[#f0f9ff] transition-all font-['Roboto:Medium',sans-serif] text-[14px] flex items-center justify-center gap-2"
                >
                  <Plus className="w-5 h-5" />
                  Add Custom Shift
                </button>
              </div>

              {/* Footer */}
              <div className="px-8 py-6 border-t border-gray-200">
                <button
                  onClick={handleSaveShifts}
                  disabled={editingShiftId !== null}
                  className={`w-full py-3.5 rounded-lg font-['Roboto:Bold',sans-serif] font-bold text-[16px] transition-all ${
                    editingShiftId !== null
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-[#61b8e7] hover:bg-[#4da3d1] text-white'
                  }`}
                  type="button"
                >
                  Save Work Shifts
                </button>

                {editingShiftId !== null && (
                  <p className="text-center font-['Roboto:Regular',sans-serif] text-[12px] text-gray-500 mt-2">
                    Please save or cancel the current edit first
                  </p>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
